// Bói Live — trò chuyện trực tiếp bằng giọng nói với thầy (Gemini Live qua BE relay).
// Tab 1: phiên live (pre-session → live → ended). Tab 2: lịch sử các phiên.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Clock,
  Coins,
  Gauge,
  HelpCircle,
  Loader2,
  Mic,
  Radio,
  Send,
  Settings,
  Trash2,
  Wallet,
  X,
  ArrowLeft,
  Eye,
  EyeOff,
  MicOff,
  Pause,
  Play,
  PhoneOff,
} from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DivinerPicker } from "@/components/diviner-picker";
import { LiveAvatarPanel } from "@/components/live/live-avatar-panel";
import { LiveCastingEffect } from "@/components/live/live-casting-effect";
import { LiveControls, formatMmSs, formatVnd } from "@/components/live/live-controls";
import { LiveSessionSummary } from "@/components/live/live-session-summary";
import {
  LiveTranscript,
  normalizeBargeInOrder,
  shouldShowLivePartial,
} from "@/components/live/live-transcript";
import { LiveResultsButton } from "@/components/live/live-results-dock";
import { LiveMicMeter } from "@/components/live/live-mic-meter";
import { useLiveSession } from "@/hooks/useLiveSession";
import { getDefaultDivinerId, useBoideUser } from "@/hooks/use-boide-user";
import { clampWords } from "@/lib/chat-limit";
import { useRegisterTour, useTour } from "@/components/tour/tour-provider";
import {
  deleteLiveSession,
  getLiveConfig,
  listLiveSessions,
  LIVE_SESSIONS_PAGE_SIZE,
  getAiContextSettings,
  updateAiContextSettings,
  type LiveDiviner,
  type LiveSpeechRate,
  type LiveSessionSummary as LiveSessionRow,
} from "@/lib/boide-api";
import { requestQuickTopup } from "@/lib/quick-topup";

export const Route = createFileRoute("/_authenticated/boi-live/")({
  head: () => ({
    meta: [
      { title: "Đàm Đạo Trực Tiếp Bằng Giọng Nói Với Thầy Bói AI" },
      {
        name: "description",
        content:
          "Gọi thoại trực tiếp với thầy bói AI: gieo quẻ Kinh Dịch, lập lá số Tử Vi và Bát Tự ngay trong lúc trò chuyện, chỉ 7.500đ/phút. Bắt đầu ngay!",
      },
    ],
  }),
  component: BoiLivePage,
});

const END_REASON_BADGE: Record<string, string> = {
  user_ended: "Hoàn tất",
  insufficient_funds: "Hết số dư",
  max_duration: "Hết giờ",
  upstream_closed: "Gián đoạn",
  error: "Lỗi",
  orphaned: "Gián đoạn",
  pause_timeout: "Hết giờ tạm dừng",
};

function BoiLivePage() {
  const { user } = useBoideUser();
  const [divinerId, setDivinerId] = useState("cu_an");
  const [speechRate, setSpeechRate] = useState<LiveSpeechRate>("slow");
  const [showTranscript, setShowTranscript] = useState(false);
  // Công tắc `live_memory` (ai-be v127) — mặc định TẮT, cùng nguồn với trang hồ sơ.
  const [personalIntelligence, setPersonalIntelligence] = useState(false);
  const [savingPersonalIntelligence, setSavingPersonalIntelligence] = useState(false);
  const live = useLiveSession();
  const topupTriggeredRef = useRef(false);

  useEffect(() => {
    const outOfFunds =
      live.endInfo?.reason === "insufficient_funds" ||
      (live.state === "live" && live.elapsedSeconds > 0 && live.billing.balanceRemaining <= 0);
    if (outOfFunds && !topupTriggeredRef.current) {
      topupTriggeredRef.current = true;
      requestQuickTopup(
        "Số dư đã hết hoặc không đủ để duy trì Bói Live. Nạp nhanh bằng QR để tiếp tục.",
        live.endInfo?.quick_topup_offer,
      );
    }
    if (live.state === "idle") topupTriggeredRef.current = false;
  }, [
    live.billing.balanceRemaining,
    live.elapsedSeconds,
    live.endInfo?.quick_topup_offer,
    live.endInfo?.reason,
    live.state,
  ]);

  const config = useQuery({
    queryKey: ["live-config"],
    queryFn: getLiveConfig,
    refetchOnWindowFocus: false,
  });

  useRegisterTour(
    live.state === "live" ? "boi-live-session" : "boi-live",
    live.state === "live" || (live.state === "idle" && !config.isLoading),
  );

  const { setTranscriptVisible } = live;

  useEffect(() => {
    const handleTourEvent = (e: Event) => {
      const { tourId, type, status, action } = (e as CustomEvent).detail || {};
      if (tourId === "boi-live-session") {
        if (type === "tour:start") {
          setTranscriptVisible(true);
        }
        if (
          type === "tour:end" ||
          status === "finished" ||
          status === "skipped" ||
          (type === "step:after" && action === "close")
        ) {
          setTranscriptVisible(false);
        }
      }
    };
    window.addEventListener("tour-event", handleTourEvent);
    return () => {
      window.removeEventListener("tour-event", handleTourEvent);
    };
  }, [setTranscriptVisible]);

  useEffect(() => {
    if (user?.profile) {
      setDivinerId(getDefaultDivinerId(user.profile));
    }
  }, [user]);

  // KHÔNG đọc `user.profile.live_personal_intelligence_enabled`: hồ sơ đến từ
  // account-be, nơi cột cũ (mặc định bật) vẫn còn — ai-be không dùng nó nữa.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    void getAiContextSettings()
      .then((s) => alive && setPersonalIntelligence(s.live_memory))
      .catch(() => alive && setPersonalIntelligence(false));
    return () => {
      alive = false;
    };
  }, [user]);

  const selectedDiviner = useMemo(
    () => config.data?.diviners.find((d) => d.id === divinerId) ?? null,
    [config.data, divinerId],
  );

  const inSession = live.state === "connecting" || live.state === "live";

  useEffect(() => {
    if (inSession) {
      document.body.classList.add("boi-live-active");
    } else {
      document.body.classList.remove("boi-live-active");
    }
    return () => {
      document.body.classList.remove("boi-live-active");
    };
  }, [inSession]);

  return (
    // Root layout là h-dvh overflow-hidden — trang phải tự tạo vùng cuộn (pattern lich-su).
    <main
      className={
        inSession
          ? "flex h-dvh w-full overflow-hidden bg-background"
          : "app-feature-page app-feature-bg app-feature-bg--boi-live flex-1 overflow-y-auto"
      }
    >
      <div className={inSession ? "w-full h-full" : "mx-auto w-full max-w-6xl px-4 py-6"}>
        {!inSession && (
          <div className="mb-4 flex items-center gap-2">
            <Radio className="h-6 w-6 text-primary" />
            <h1 className="font-serif-soft text-2xl text-gold-gradient">Bói Live</h1>
            <Badge variant="outline" className="border-gold-soft/40">
              Beta
            </Badge>
          </div>
        )}

        {inSession || live.state === "ended" ? (
          <LiveSessionView live={live} diviner={selectedDiviner} />
        ) : (
          <Tabs defaultValue="live">
            <TabsList className="mb-4">
              <TabsTrigger value="live">Bói Live</TabsTrigger>
              <TabsTrigger value="history">Lịch sử</TabsTrigger>
            </TabsList>
            <TabsContent value="live">
              <PreSession
                divinerId={divinerId}
                setDivinerId={setDivinerId}
                speechRate={speechRate}
                setSpeechRate={setSpeechRate}
                showTranscript={showTranscript}
                setShowTranscript={setShowTranscript}
                personalIntelligence={personalIntelligence}
                savingPersonalIntelligence={savingPersonalIntelligence}
                setPersonalIntelligence={(enabled) => {
                  const previous = personalIntelligence;
                  setPersonalIntelligence(enabled);
                  setSavingPersonalIntelligence(true);
                  void updateAiContextSettings({ live_memory: enabled })
                    .then((s) => setPersonalIntelligence(s.live_memory))
                    .catch((err) => {
                      setPersonalIntelligence(previous);
                      toast.error(err instanceof Error ? err.message : "Không lưu được cài đặt");
                    })
                    .finally(() => setSavingPersonalIntelligence(false));
                }}
                configQuery={config}
                error={live.error}
                onStart={() => {
                  live.setTranscriptVisible(showTranscript);
                  void live.start(divinerId, speechRate);
                }}
              />
            </TabsContent>
            <TabsContent value="history">
              <HistoryList />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </main>
  );
}

// ─── Pre-session ────────────────────────────────────────────────────────────

function PreSession({
  divinerId,
  setDivinerId,
  speechRate,
  setSpeechRate,
  showTranscript,
  setShowTranscript,
  personalIntelligence,
  savingPersonalIntelligence,
  setPersonalIntelligence,
  configQuery,
  error,
  onStart,
}: {
  divinerId: string;
  setDivinerId: (id: string) => void;
  speechRate: LiveSpeechRate;
  setSpeechRate: (rate: LiveSpeechRate) => void;
  showTranscript: boolean;
  setShowTranscript: (val: boolean) => void;
  personalIntelligence: boolean;
  savingPersonalIntelligence: boolean;
  setPersonalIntelligence: (val: boolean) => void;
  configQuery: ReturnType<typeof useQuery<Awaited<ReturnType<typeof getLiveConfig>>>>;
  error: string | null;
  onStart: () => void;
}) {
  const cfg = configQuery.data;
  const affordableMinutes = cfg ? Math.floor(cfg.affordable_seconds / 60) : 0;
  const canStart =
    !!cfg && cfg.enabled && cfg.balance >= cfg.min_start_seconds * cfg.price_per_second;

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <Card className="border-gold-soft/40 bg-card/60 shadow-mystic">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mic className="h-4 w-4 text-primary" /> Trò chuyện trực tiếp với thầy
          </CardTitle>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
                title="Cài đặt phiên thoại"
                data-tour="boi-live-settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[340px] sm:max-w-sm rounded-2xl border-gold-soft/30 bg-background/95 p-5">
              <DialogHeader>
                <DialogTitle className="text-gold-gradient font-serif-soft flex items-center gap-2 text-base">
                  <Settings className="h-4 w-4 text-primary" /> Cài đặt phiên thoại
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Tùy chỉnh tốc độ nói và hiển thị hội thoại của thầy.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-3 text-sm">
                {/* 1. Speech Rate */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground block">
                    TỐC ĐỘ NÓI CỦA THẦY
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {(
                      [
                        ["slow", "Chậm"],
                        ["normal", "Vừa"],
                        ["fast", "Nhanh"],
                      ] as const
                    ).map(([value, label]) => (
                      <Button
                        key={value}
                        type="button"
                        variant={speechRate === value ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSpeechRate(value)}
                        className="text-xs h-8 w-full"
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-normal mt-1">
                    Mặc định là Chậm để dễ nghe và theo dõi phần luận giải.
                  </p>
                </div>

                {/* 2. Show Transcript Switch */}
                <div className="flex items-center justify-between pt-3 border-t border-gold-soft/10">
                  <div className="space-y-0.5">
                    <label className="text-xs font-semibold text-muted-foreground block">
                      HIỂN THỊ TRANSCRIPT
                    </label>
                    <span className="text-[10px] text-muted-foreground leading-none">
                      Hiện phụ đề nội dung đàm đạo
                    </span>
                  </div>
                  <Switch checked={showTranscript} onCheckedChange={setShowTranscript} />
                </div>

                <div className="flex items-center justify-between gap-4 pt-3 border-t border-gold-soft/10">
                  <div className="space-y-0.5">
                    <label className="text-xs font-semibold text-muted-foreground block">
                      TRÍ THÔNG MINH CÁ NHÂN
                    </label>
                    <span className="block text-[10px] leading-normal text-muted-foreground">
                      Dùng lịch sử Bói Live để AI hiểu bạn hơn trong những lần sau
                    </span>
                  </div>
                  <Switch
                    checked={personalIntelligence}
                    disabled={savingPersonalIntelligence}
                    onCheckedChange={setPersonalIntelligence}
                  />
                </div>
              </div>

              <DialogClose asChild>
                <Button className="w-full bg-gold-gradient text-white hover:text-white hover:brightness-110 mt-2 text-xs font-medium h-9 rounded-xl">
                  Hoàn tất
                </Button>
              </DialogClose>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Nói chuyện bằng giọng nói như một buổi xem thật: thầy nghe con kể, gieo quẻ Kinh Dịch,
            lập lá số Tử Vi / Bát Tự ngay trong lúc trò chuyện.
          </p>

          <ul className="space-y-1.5 rounded-xl border border-gold-soft/20 bg-background/30 p-3 text-xs text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary">✦</span>
              Nói tự nhiên như gọi điện — muốn chen lời lúc nào cứ nói, thầy sẽ dừng ngay để nghe
              bạn.
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✦</span>
              Quay lại xem tiếp, thầy vẫn nhớ chuyện lần trước của bạn.
            </li>
          </ul>

          <div className="flex justify-center" data-tour="boi-live-picker">
            <DivinerPicker value={divinerId} onChange={setDivinerId} />
          </div>

          <div data-tour="boi-live-pricing">
            {configQuery.isLoading ? (
              <Skeleton className="h-20 w-full rounded-xl" />
            ) : cfg ? (
              <div className="rounded-xl border border-gold-soft/30 bg-background/40 p-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground shrink-0">Giá</span>
                  <span className="font-medium text-right">
                    {formatVnd(cfg.price_per_minute)}/phút{" "}
                    <span className="text-muted-foreground">
                      (1 phút có thể hỏi được từ 2 đến 3 câu hỏi)
                    </span>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Số dư</span>
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <Wallet className="h-3.5 w-3.5 text-primary" /> {formatVnd(cfg.balance)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Đủ cho khoảng</span>
                  <span className="tabular-nums">≈ {affordableMinutes} phút</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-destructive">Không tải được cấu hình Bói Live.</p>
            )}
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          {cfg && !cfg.enabled ? (
            <p className="text-sm text-amber-500">Bói Live đang tạm đóng, quay lại sau nhé.</p>
          ) : null}

          <Button
            className="w-full"
            size="lg"
            disabled={!canStart}
            onClick={onStart}
            data-tour="boi-live-start-cta"
          >
            <Mic className="mr-2 h-4 w-4" /> Bắt đầu trò chuyện
          </Button>
          {cfg && cfg.enabled && !canStart ? (
            <p className="text-center text-sm text-muted-foreground">
              Cần tối thiểu {formatVnd(cfg.min_start_seconds * cfg.price_per_second)} để bắt đầu.{" "}
              <Link to="/thanh-toan" className="text-primary underline">
                Nạp thêm
              </Link>
            </p>
          ) : (
            <p className="text-center text-xs text-muted-foreground">
              Trình duyệt sẽ xin quyền micro khi bắt đầu. Phí tính theo thời gian trò chuyện thực
              tế.
            </p>
          )}
          <p className="text-center text-[11px] text-muted-foreground/70">
            Trải nghiệm cùng thầy bói AI — nội dung mang tính chiêm nghiệm, tham khảo.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── In-session + ended ─────────────────────────────────────────────────────

function UserSoundwave({ analyser, active }: { analyser: AnalyserNode | null; active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let rafId = 0;
    const bufferLength = analyser ? analyser.frequencyBinCount : 0;
    const dataArray = new Uint8Array(bufferLength);

    let phase = 0;

    const draw = () => {
      rafId = requestAnimationFrame(draw);
      const w = (canvas.width = canvas.clientWidth);
      const h = (canvas.height = canvas.clientHeight);

      ctx.clearRect(0, 0, w, h);

      let volume = 0;
      if (active && analyser) {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        volume = sum / bufferLength;
      }

      // Convert volume level to scale (adding a noise gate threshold of 5 to ignore mic background hum)
      const amplitudeScale = active && volume > 5 ? Math.min((volume - 5) / 45, 1.5) : 0;

      // Luôn hiện rõ — không ẩn/mờ dần theo âm lượng: mic im thì đường thẳng, có tiếng thì nhảy sóng.
      ctx.globalAlpha = 1;

      // Draw three overlapping sine waves
      const drawWave = (
        color: string,
        amp: number,
        freq: number,
        phaseShift: number,
        lineWidth: number,
      ) => {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = "round";

        for (let x = 0; x < w; x++) {
          const edgeFactor = Math.sin((x / w) * Math.PI);
          const y =
            h / 2 + Math.sin(x * freq + phase + phaseShift) * amp * amplitudeScale * edgeFactor;

          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      };

      phase += active && volume > 10 ? 0.15 : 0.04;

      if (active && amplitudeScale > 0.05) {
        drawWave("rgba(245, 158, 11, 0.15)", 20, 0.015, 0, 2);
        drawWave("rgba(251, 191, 36, 0.25)", 15, 0.02, Math.PI / 3, 1.5);
        drawWave("rgba(212, 163, 89, 0.55)", 10, 0.025, Math.PI / 1.5, 3);
      } else {
        // Mic không thu được gì đáng kể (hoặc đang tắt) — đường thẳng mặc định, luôn rõ.
        // amp=0 nên bất kể freq/phase, y luôn = h/2 → đường kẻ ngang tuyệt đối.
        drawWave("rgba(212, 163, 89, 0.5)", 0, 0.01, 0, 2);
      }
    };

    draw();

    return () => cancelAnimationFrame(rafId);
  }, [analyser, active]);

  return <canvas ref={canvasRef} className="h-10 w-full opacity-80" />;
}

function YinYang({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={className} fill="currentColor">
      <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="4" />
      <path
        d="M 50 4 A 46 46 0 0 0 50 96 A 23 23 0 0 0 50 50 A 23 23 0 0 1 50 4 M 50 19.5 A 7.5 7.5 0 1 0 50 34.5 A 7.5 7.5 0 1 0 50 19.5 M 50 65.5 A 7.5 7.5 0 1 0 50 80.5 A 7.5 7.5 0 1 0 50 65.5 Z"
        fillRule="evenodd"
        fill="currentColor"
      />
    </svg>
  );
}

function DivinerAvatarPulse({
  diviner,
  divinerSpeaking,
  playerAnalyser,
  pendingTool,
  pendingToolLabel,
  coinsTossing,
}: {
  diviner: LiveDiviner | null;
  divinerSpeaking: boolean;
  playerAnalyser: AnalyserNode | null;
  pendingTool: string | null;
  pendingToolLabel: string | null;
  coinsTossing: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!playerAnalyser || !divinerSpeaking) {
      if (containerRef.current) {
        containerRef.current.style.setProperty("--avatar-scale", "1");
        containerRef.current.style.setProperty("--ripple-opacity", "0");
      }
      return;
    }
    const bufferLength = playerAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const interval = setInterval(() => {
      if (playerAnalyser) {
        playerAnalyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const avg = sum / dataArray.length;
        const scaleVal = 1 + (avg / 120) * 0.15; // scale range up to 1.15
        const opacityVal = 0.2 + (avg / 255) * 0.8;
        if (containerRef.current) {
          containerRef.current.style.setProperty("--avatar-scale", `${scaleVal}`);
          containerRef.current.style.setProperty("--ripple-opacity", `${opacityVal}`);
        }
      }
    }, 50);
    return () => clearInterval(interval);
  }, [playerAnalyser, divinerSpeaking]);

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-col items-center select-none ${
        divinerSpeaking ? "voice-pulse-active" : ""
      }`}
      style={
        {
          "--avatar-scale": "1",
          "--ripple-opacity": "0",
        } as React.CSSProperties
      }
    >
      {/* Concentric ripple rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="voice-pulse-ring voice-pulse-ring-1"
          style={{ opacity: "var(--ripple-opacity)" } as React.CSSProperties}
        />
        <div
          className="voice-pulse-ring voice-pulse-ring-2"
          style={{ opacity: "calc(var(--ripple-opacity) * 0.7)" } as React.CSSProperties}
        />
        <div
          className="voice-pulse-ring voice-pulse-ring-3"
          style={{ opacity: "calc(var(--ripple-opacity) * 0.4)" } as React.CSSProperties}
        />
      </div>

      {/* Main Avatar Container */}
      <div
        className="relative rounded-full p-1.5 transition-transform duration-75 border border-gold-soft/30 bg-background/40"
        style={{
          transform: "scale(var(--avatar-scale))",
        }}
      >
        <Avatar className="h-28 w-28 md:h-36 md:w-36 shadow-[0_0_32px_rgba(212,163,89,0.25)] border border-amber-400/20">
          <AvatarImage
            src={diviner?.avatar}
            alt={diviner?.ten}
            className="object-cover object-center"
            draggable={false}
          />
          <AvatarFallback className="text-3xl bg-gold-gradient text-primary-foreground">
            {diviner?.ten?.[0] ?? "?"}
          </AvatarFallback>
        </Avatar>

        {/* Dynamic spinner or yin-yang bagua spinner */}
        {pendingTool === "gieo_que_kinh_dich" ? (
          <div className="absolute inset-1.5 bg-black/20 rounded-full flex items-center justify-center animate-fade-in z-20">
            <YinYang className="w-12 h-12 text-primary animate-bagua" />
          </div>
        ) : pendingToolLabel ? (
          <div className="absolute inset-1.5 bg-black/30 rounded-full flex items-center justify-center z-20">
            <span className="w-3 h-3 bg-amber-400 rounded-full animate-ping" />
          </div>
        ) : null}
      </div>

      {/* Tossing coins flight animation */}
      {pendingTool === "gieo_que_kinh_dich" && coinsTossing && (
        <div className="absolute inset-0 z-30 pointer-events-none">
          {[
            { mx: "-55px", fx: "-85px", fy: "80px", delay: "0ms" },
            { mx: "0px", fx: "0px", fy: "100px", delay: "80ms" },
            { mx: "55px", fx: "85px", fy: "80px", delay: "160ms" },
          ].map((c, i) => (
            <div
              key={i}
              className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 animate-coin-fly"
              style={{
                ["--mx" as string]: c.mx,
                ["--fx" as string]: c.fx,
                ["--fy" as string]: c.fy,
                animationDelay: c.delay,
              }}
            >
              <div className="coin-trail relative h-full w-full rounded-full bg-gradient-to-br from-[oklch(0.85_0.16_85)] via-[oklch(0.65_0.14_75)] to-[oklch(0.45_0.10_70)] shadow-[0_0_15px_oklch(0.82_0.14_85/0.8)]">
                <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-[oklch(0.20_0.05_70)] bg-[oklch(0.20_0.05_70)]" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveSessionView({
  live,
  diviner,
}: {
  live: ReturnType<typeof useLiveSession>;
  diviner: LiveDiviner | null;
}) {
  const [text, setText] = useState("");
  const [coinsTossing, setCoinsTossing] = useState(false);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const { hasActiveTour, startTour } = useTour();
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      setIsMobileLayout(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (live.pendingTool !== "gieo_que_kinh_dich") {
      setCoinsTossing(false);
      return;
    }
    setCoinsTossing(true);
    const interval = setInterval(() => {
      setCoinsTossing(false);
      setTimeout(() => setCoinsTossing(true), 80);
    }, 3200);
    return () => clearInterval(interval);
  }, [live.pendingTool]);

  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [live.transcript.length, live.userPartial, live.divinerPartial]);

  const [tossingProgress, setTossingProgress] = useState(0);
  const [finalTossActive, setFinalTossActive] = useState(false);
  const pressTimerRef = useRef<number | null>(null);

  const startPress = () => {
    if (pressTimerRef.current || finalTossActive) return;
    const startTime = Date.now();
    const duration = 1800; // 1.8s
    const update = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setTossingProgress(progress);
      if (progress < 1) {
        pressTimerRef.current = requestAnimationFrame(update);
      } else {
        pressTimerRef.current = null;
        setFinalTossActive(true);
        setCoinsTossing(true);

        // Wait 2.2s for the coins to float and land gracefully before submitting the API call
        setTimeout(() => {
          live.castQue();
          setFinalTossActive(false);
          setTossingProgress(0);
        }, 2200);
      }
    };
    pressTimerRef.current = requestAnimationFrame(update);
  };

  const stopPress = () => {
    if (pressTimerRef.current) {
      cancelAnimationFrame(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    setTossingProgress(0);
  };

  // Sau khi kết thúc: tìm id bản ghi lịch sử theo session_uuid để link "Xem lại"
  const latest = useQuery({
    queryKey: ["live-sessions-latest", live.sessionUuid],
    queryFn: () => listLiveSessions(1, 5),
    enabled: live.state === "ended" && !!live.sessionUuid,
  });
  const historyId = useMemo(() => {
    if (live.state !== "ended") return undefined;
    const items = latest.data?.items ?? [];
    return items.find((x) => x.session_uuid === live.sessionUuid)?.id;
  }, [latest.data, live.state, live.sessionUuid]);

  const runningCost = Math.max(
    live.billing.amountChargedTotal,
    live.elapsedSeconds * live.billing.pricePerSecond,
  );

  // ─── CONNECTING & ACTIVE LIVE SESSION (Immersive UI) ──────────────────────
  if (live.state === "connecting" || live.state === "live") {
    const isConnecting = live.state === "connecting";

    if (isMobileLayout) {
      return (
        <div className="relative w-full h-full flex flex-col justify-between overflow-hidden bg-background text-foreground md:rounded-2xl md:border border-white/5 md:shadow-2xl">
          {/* 1. Immersive Fullscreen Background */}
          {diviner?.avatar && (
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
              <img src={diviner.avatar} alt={diviner.ten} className="w-full h-full object-cover" />
              {/* Dark overlay gradients for text readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-black/90" />
              {/* Subtle animated background aura */}
              <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent animate-pulse duration-[6000ms]" />

              {/* Voice responsive screen border glow (static, non-shaking) */}
              <div
                className={`absolute inset-0 z-10 pointer-events-none transition-all duration-300 border-2 rounded-2xl ${
                  live.divinerSpeaking
                    ? "border-amber-400/20 shadow-[inset_0_0_24px_rgba(251,191,36,0.15)]"
                    : "border-transparent shadow-none"
                }`}
              />
            </div>
          )}

          {/* Inline keyframes for Diviner voice equalizer */}
          <style>{`
            @keyframes diviner-soundwave {
              0%, 100% { height: 4px; }
              50% { height: 16px; }
            }
            .diviner-bar-1 { animation: diviner-soundwave 0.8s infinite 0.1s ease-in-out; }
            .diviner-bar-2 { animation: diviner-soundwave 0.8s infinite 0.3s ease-in-out; }
            .diviner-bar-3 { animation: diviner-soundwave 0.8s infinite 0.2s ease-in-out; }
            .diviner-bar-4 { animation: diviner-soundwave 0.8s infinite 0.4s ease-in-out; }
            
            /* Slower, smoother coin flying animation for Boi Live */
            .animate-coin-fly {
              animation: coin-fly 2.5s cubic-bezier(0.25, 1, 0.5, 1) forwards !important;
            }
          `}</style>

          {/* 2. Top Header Controls (Mockup Style) */}
          <div className="relative z-10 flex items-center justify-between p-4 gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={live.end}
              className="h-10 w-10 rounded-full bg-black/35 hover:bg-black/50 text-white border border-white/10 active:scale-95 transition-all cursor-pointer shrink-0"
              aria-label="Quay lại"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>

            {/* Canh giữa tuyệt đối theo màn hình — nút trái/badge phải khác bề rộng
                nên để trong flow justify-between sẽ bị lệch */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              {!isConnecting ? (
                <div className="flex items-center gap-1.5 bg-black/35 backdrop-blur-md border border-white/10 rounded-full px-3.5 py-1.5 text-white font-sans text-xs font-semibold tabular-nums shadow-lg">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                  <span>{formatMmSs(live.elapsedSeconds)}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 bg-black/35 backdrop-blur-md border border-white/10 rounded-full px-3.5 py-1.5 text-white font-sans text-xs font-semibold shadow-lg animate-pulse whitespace-nowrap">
                  <span>Đang kết nối...</span>
                </div>
              )}
            </div>

            {!isConnecting && (
              <div className="flex flex-col items-end bg-black/35 backdrop-blur-md border border-white/10 rounded-2xl px-3.5 py-1 text-white font-sans text-xs shadow-lg">
                <div className="flex items-center gap-1 font-semibold">
                  <Coins className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  <span className="tabular-nums">{formatVnd(runningCost)}</span>
                </div>
                <div className="text-[9px] text-white/50 font-normal">
                  {formatVnd(Math.round(live.billing.pricePerSecond * 60))}/phút
                </div>
              </div>
            )}
          </div>

          {/* 3. Middle Area: Active tools or coin tossing */}
          <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-4">
            {/* Tossing coins flight animation (Mobile) */}
            {((live.pendingTool === "gieo_que_kinh_dich" && coinsTossing) || finalTossActive) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                {[
                  { mx: "-55px", fx: "-85px", fy: "80px", delay: "0ms" },
                  { mx: "0px", fx: "0px", fy: "100px", delay: "80ms" },
                  { mx: "55px", fx: "85px", fy: "80px", delay: "160ms" },
                ].map((c, i) => (
                  <div
                    key={i}
                    className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 animate-coin-fly"
                    style={{
                      ["--mx" as string]: c.mx,
                      ["--fx" as string]: c.fx,
                      ["--fy" as string]: c.fy,
                      animationDelay: c.delay,
                    }}
                  >
                    <div className="coin-trail relative h-full w-full rounded-full bg-gradient-to-br from-[oklch(0.85_0.16_85)] via-[oklch(0.65_0.14_75)] to-[oklch(0.45_0.10_70)] shadow-[0_0_15px_oklch(0.82_0.14_85/0.8)]">
                      <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-[oklch(0.20_0.05_70)] bg-[oklch(0.20_0.05_70)]" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {isConnecting ? (
              <div className="flex flex-col items-center gap-4 text-center animate-mystic-rise">
                <div className="relative w-16 h-16 flex items-center justify-center">
                  <div className="absolute inset-0 border-2 border-dashed border-primary/40 rounded-full animate-spin duration-[10000ms]" />
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
                <p className="text-sm font-medium text-white/90 font-serif-soft tracking-wider">
                  Đang kết nối với {diviner?.ten ?? "thầy"}…
                </p>
              </div>
            ) : live.pendingToolLabel && live.pendingTool !== "gieo_que_kinh_dich" ? (
              <div className="flex flex-col items-center gap-4 text-center animate-mystic-rise">
                <div className="relative w-16 h-16 flex items-center justify-center">
                  <div className="absolute inset-0 border-2 border-dashed border-primary/40 rounded-full animate-spin duration-[10000ms]" />
                  <Radio className="w-8 h-8 text-primary animate-pulse" />
                </div>
                <p className="text-sm font-medium text-primary font-serif-soft tracking-wider animate-pulse">
                  {live.pendingToolLabel}
                </p>
              </div>
            ) : (
              <>
                {/* Tossing Coins flights loader (Mobile only) */}
                {live.awaitingCast && !finalTossActive && (
                  <div className="flex flex-col items-center justify-center gap-4 py-4 w-full max-w-xs mx-auto animate-mystic-rise">
                    <p className="text-xs font-serif-soft text-amber-300 tracking-widest animate-pulse text-center font-bold">
                      ✦ TỊNH TÂM TỤ KHÍ · KHỞI LẬP QUẺ DỊCH ✦
                    </p>

                    <Button
                      onMouseDown={startPress}
                      onMouseUp={stopPress}
                      onMouseLeave={stopPress}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        startPress();
                      }}
                      onTouchEnd={stopPress}
                      className="w-full h-16 rounded-2xl bg-gold-gradient text-black font-semibold shadow-gold active:scale-95 transition-all select-none duration-150 cursor-pointer flex flex-col justify-center gap-0.5 border border-amber-400/20"
                    >
                      <span className="text-sm font-bold tracking-wide">
                        {tossingProgress > 0
                          ? `Đang tụ khí thỉnh quẻ... ${Math.round(tossingProgress * 100)}%`
                          : "Nhấn & Giữ để Gieo Quẻ 🪙"}
                      </span>
                      <span className="text-[9px] opacity-75 font-normal tracking-wider">
                        (Nhắm mắt nghĩ về câu hỏi & giữ 1.8s)
                      </span>
                    </Button>

                    <div className="w-full bg-amber-950/20 h-1.5 rounded-full overflow-hidden">
                      <div
                        className="bg-primary h-full transition-all duration-75"
                        style={{ width: `${tossingProgress * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 4. Bottom Info, Chat Input & Translucent Controls */}
          {!isConnecting && (
            <div className="relative z-10 p-4 pb-8 space-y-4 max-w-md w-full mx-auto flex flex-col justify-end">
              {/* Diviner details (Name & Verified Badge) */}
              <div className="text-left space-y-0.5 pl-1.5">
                <div className="flex items-center gap-1.5 text-white">
                  <h2 className="font-serif-soft text-2xl font-bold tracking-wide">
                    {diviner?.ten}
                  </h2>
                  <span className="flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-black shadow-md mr-0.5">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3 w-3 fill-current"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  </span>

                  {/* Speaking Equalizer Wave Indicator next to verified badge */}
                  <div className="ml-1 flex items-center justify-center">
                    {live.divinerSpeaking ? (
                      <div className="flex items-end gap-0.75 h-4.5 pb-0.5 shrink-0">
                        <span className="w-0.75 bg-amber-400 rounded-full diviner-bar-1" />
                        <span className="w-0.75 bg-amber-400 rounded-full diviner-bar-2" />
                        <span className="w-0.75 bg-amber-400 rounded-full diviner-bar-3" />
                        <span className="w-0.75 bg-amber-400 rounded-full diviner-bar-4" />
                      </div>
                    ) : (
                      <div className="flex items-end gap-0.75 h-4.5 pb-0.5 shrink-0 opacity-40">
                        <span className="w-0.75 h-1 bg-white rounded-full" />
                        <span className="w-0.75 h-1 bg-white rounded-full" />
                        <span className="w-0.75 h-1 bg-white rounded-full" />
                        <span className="w-0.75 h-1 bg-white rounded-full" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Subtitles/Live transcripts overlay */}
              {live.transcriptVisible && (
                <div
                  data-tour="boi-live-transcript"
                  className="w-full h-24 overflow-y-auto px-4 py-2.5 rounded-2xl bg-black/45 border border-white/5 text-left space-y-1.5 scrollbar-none text-[11px] leading-relaxed shadow-lg backdrop-blur-md"
                >
                  {live.transcript.length === 0 && !live.userPartial && !live.divinerPartial && (
                    <p className="text-white/45 text-center italic py-4 text-[10px]">
                      Hãy đàm đạo để xem phụ đề hội thoại...
                    </p>
                  )}
                  {normalizeBargeInOrder(
                    [...live.transcript]
                      .sort((a, b) => {
                        if (a.order !== undefined && b.order !== undefined)
                          return a.order - b.order;
                        return a.t - b.t;
                      })
                      .map((segment) => ({
                        kind: "segment" as const,
                        t: segment.t,
                        order: segment.order,
                        segment,
                      })),
                  ).map((t, idx) => {
                    if (t.kind !== "segment") return null;
                    const segment = t.segment;
                    const isUser = segment.role === "user";
                    return (
                      <div
                        key={idx}
                        className={
                          isUser
                            ? "text-white/85 text-xs font-normal"
                            : "text-amber-300 font-medium font-serif-soft text-[13px] leading-relaxed"
                        }
                      >
                        <span className="font-semibold">
                          {isUser ? "Bạn" : (diviner?.ten ?? "Thầy")}:
                        </span>{" "}
                        {segment.text}
                      </div>
                    );
                  })}

                  {/* Speaking text preview */}
                  {shouldShowLivePartial(live.transcript, "diviner", live.divinerPartial) && (
                    <div className="text-amber-300 font-medium font-serif-soft text-[13px] leading-relaxed animate-pulse">
                      <span className="font-semibold">{diviner?.ten ?? "Thầy"}:</span>{" "}
                      {live.divinerPartial}
                    </div>
                  )}
                  {shouldShowLivePartial(live.transcript, "user", live.userPartial) && (
                    <div className="text-white/60 text-xs italic animate-pulse">
                      <span className="font-semibold not-italic">Bạn:</span> {live.userPartial}
                    </div>
                  )}
                  <div ref={transcriptEndRef} />
                </div>
              )}

              {!live.awaitingCast && (
                <>
                  {/* Translucent Chat input field */}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (live.sendText(text)) setText("");
                    }}
                    className="w-full"
                  >
                    <div
                      data-tour="boi-live-text-input"
                      className="relative flex items-center bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-3.5 py-1.5 shadow-lg"
                    >
                      <Input
                        value={text}
                        onChange={(e) => setText(clampWords(e.target.value))}
                        placeholder={live.paused ? "Đang tạm dừng…" : "Nhập câu hỏi của bạn..."}
                        disabled={live.paused}
                        className="flex-1 bg-transparent border-0 text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm h-8 py-0 focus:outline-none disabled:opacity-50"
                      />
                      <Button
                        type="submit"
                        size="icon"
                        variant="ghost"
                        disabled={live.paused}
                        className="h-8 w-8 rounded-full text-white/70 hover:text-white hover:bg-white/10 active:scale-95 cursor-pointer shrink-0"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>

                  {/* Siri-like voice waves */}
                  <div className="w-full h-8 flex items-center justify-center overflow-hidden">
                    <UserSoundwave
                      analyser={live.micAnalyser}
                      active={live.micEnabled && !live.paused}
                    />
                  </div>

                  {/* Custom round controls panel */}
                  <div className="flex items-center justify-center gap-3">
                    {/* Results Button */}
                    <div data-tour="boi-live-results" className="shrink-0">
                      <LiveResultsButton results={live.toolResults} />
                    </div>

                    {/* Subtitles toggle */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => live.setTranscriptVisible(!live.transcriptVisible)}
                      className="h-12 w-12 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-white hover:bg-white/20 active:scale-95 transition-all shadow-lg shrink-0 cursor-pointer"
                      title={live.transcriptVisible ? "Ẩn phụ đề" : "Hiện phụ đề"}
                    >
                      {live.transcriptVisible ? (
                        <Eye className="h-5 w-5" />
                      ) : (
                        <EyeOff className="h-5 w-5" />
                      )}
                    </Button>

                    {/* Mic Toggle */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={live.toggleMic}
                      className={`h-12 w-12 rounded-full backdrop-blur-md border text-white transition-all shadow-lg shrink-0 cursor-pointer ${
                        live.micEnabled
                          ? "bg-white/10 border-white/10 hover:bg-white/20"
                          : "bg-red-500/20 border-red-500/35 hover:bg-red-500/30 text-red-300"
                      }`}
                      title={live.micEnabled ? "Tắt mic" : "Bật mic"}
                    >
                      {live.micEnabled ? (
                        <Mic className="h-5 w-5" />
                      ) : (
                        <MicOff className="h-5 w-5" />
                      )}
                    </Button>

                    {/* Pause/Play Toggle */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={live.togglePause}
                      className={`h-12 w-12 rounded-full backdrop-blur-md border text-white transition-all shadow-lg shrink-0 cursor-pointer ${
                        live.paused
                          ? "bg-amber-500/20 border-amber-500/35 hover:bg-amber-500/30 text-amber-300 animate-pulse"
                          : "bg-white/10 border-white/10 hover:bg-white/20"
                      }`}
                      title={live.paused ? "Tiếp tục" : "Tạm dừng"}
                    >
                      {live.paused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                    </Button>

                    {/* Red Hangup button */}
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={live.end}
                      className="h-12 w-12 rounded-full bg-red-600 hover:bg-red-700 active:scale-95 transition-all shadow-[0_4px_20px_rgba(220,38,38,0.4)] border border-red-500/30 shrink-0 cursor-pointer"
                      title="Kết thúc cuộc gọi"
                    >
                      <PhoneOff className="h-5 w-5" />
                    </Button>
                  </div>

                  {live.paused && (
                    <p className="text-center text-xs font-semibold text-amber-400 animate-pulse">
                      Đang tạm dừng — không tính phí, mic đã tắt
                    </p>
                  )}
                </>
              )}

              {live.balanceLow && (
                <p className="text-center text-xs text-amber-400 font-semibold animate-pulse">
                  Số dư sắp hết — phiên sẽ tự kết thúc khi hết tiền.
                </p>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="relative w-full h-full flex flex-col justify-between overflow-hidden bg-background text-foreground md:rounded-2xl md:border border-white/5 md:shadow-2xl">
        {/* 1. Immersive Background */}
        {diviner?.avatar && (
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
            <img
              src={diviner.avatar}
              alt=""
              className="w-full h-full object-cover filter blur-[70px] opacity-25 scale-125"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/90" />
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent animate-pulse duration-[6000ms]" />
          </div>
        )}

        {/* 2. Top Header Controls */}
        <div className="relative z-10 flex items-center p-4">
          {isConnecting ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={live.end}
              className="h-10 w-10 rounded-full border border-gold-soft/20 dark:border-white/10 bg-card/60 dark:bg-black/20 text-foreground/80 dark:text-white/80 hover:bg-accent hover:text-foreground dark:hover:bg-black/40 dark:hover:text-white transition-all active:scale-95 cursor-pointer"
              aria-label="Kết thúc phiên"
            >
              <X className="h-5 w-5" />
            </Button>
          ) : (
            hasActiveTour && (
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="h-10 w-10 rounded-full border border-gold-soft/20 dark:border-white/10 bg-card/60 dark:bg-black/20 text-foreground/80 dark:text-white/80 hover:bg-accent hover:text-foreground dark:hover:bg-black/40 dark:hover:text-white transition-all active:scale-95 cursor-pointer"
                aria-label="Xem hướng dẫn"
                title="Xem hướng dẫn"
              >
                <HelpCircle className="h-5 w-5 text-primary" />
              </Button>
            )
          )}

          {!isConnecting && (
            <div className="ml-auto flex flex-col items-end gap-0.5 text-right font-sans text-xs bg-card/65 dark:bg-black/35 backdrop-blur-md border border-gold-soft/20 dark:border-white/5 rounded-2xl px-3.5 py-1.5 text-foreground/70 dark:text-white/70 shadow-lg">
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary" />
                <span className="font-semibold tabular-nums text-foreground">
                  {formatMmSs(live.elapsedSeconds)}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Coins className="h-3.5 w-3.5 text-primary" />
                <span className="font-semibold tabular-nums text-primary">
                  {formatVnd(runningCost)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 3. Middle Area: Floating avatar or active tools */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-4">
          {isConnecting ? (
            <div className="flex flex-col items-center gap-4 text-center animate-mystic-rise">
              <div className="relative w-16 h-16 flex items-center justify-center">
                <div className="absolute inset-0 border-2 border-dashed border-primary/40 rounded-full animate-spin duration-[10000ms]" />
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
              <p className="text-sm font-medium text-foreground/80 font-serif-soft tracking-wider">
                Đang kết nối với {diviner?.ten ?? "thầy"}…
              </p>
            </div>
          ) : live.pendingToolLabel && live.pendingTool !== "gieo_que_kinh_dich" ? (
            <div className="flex flex-col items-center gap-4 text-center animate-mystic-rise">
              <div className="relative w-16 h-16 flex items-center justify-center">
                <div className="absolute inset-0 border-2 border-dashed border-primary/40 rounded-full animate-spin duration-[10000ms]" />
                <Radio className="w-8 h-8 text-primary animate-pulse" />
              </div>
              <p className="text-sm font-medium text-primary font-serif-soft tracking-wider animate-pulse">
                {live.pendingToolLabel}
              </p>
            </div>
          ) : (
            <div
              data-tour="boi-live-avatar-desktop"
              className="flex flex-col items-center justify-center gap-5"
            >
              <DivinerAvatarPulse
                diviner={diviner}
                divinerSpeaking={live.divinerSpeaking}
                playerAnalyser={live.playerAnalyser}
                pendingTool={live.pendingTool}
                pendingToolLabel={live.pendingToolLabel}
                coinsTossing={coinsTossing}
              />
              <div className="text-center max-w-xs">
                <h2 className="font-serif-soft text-xl text-gold-gradient font-semibold tracking-wide">
                  {diviner?.ten}
                </h2>
                {live.returning && (
                  <p className="mt-1.5 text-[10px] text-primary/80 border border-primary/20 bg-primary/5 px-2.5 py-0.5 rounded-full inline-block">
                    ✦ Nhớ lần đàm đạo trước của bạn
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 4. Bottom Controls */}
        {!isConnecting && (
          <div className="relative z-10 p-4 pb-8 space-y-4 max-w-md w-full mx-auto">
            {/* Floating Live Transcript/Subtitles Overlay */}
            {live.transcriptVisible && (
              <div
                data-tour="boi-live-transcript"
                className="w-full h-28 overflow-y-auto px-4 py-2.5 rounded-xl glass-panel text-left space-y-1.5 scrollbar-none text-[11px] leading-relaxed mb-1 shadow-md"
              >
                {live.transcript.length === 0 && !live.userPartial && !live.divinerPartial && (
                  <p className="text-muted-foreground text-center italic py-6 text-[10px]">
                    Bắt đầu nói chuyện để xem phụ đề đàm đạo...
                  </p>
                )}
                {normalizeBargeInOrder(
                  [...live.transcript]
                    .sort((a, b) => {
                      if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
                      return a.t - b.t;
                    })
                    .map((segment) => ({
                      kind: "segment" as const,
                      t: segment.t,
                      order: segment.order,
                      segment,
                    })),
                ).map((t, idx) => {
                  if (t.kind !== "segment") return null;
                  const segment = t.segment;
                  const isUser = segment.role === "user";
                  return (
                    <div
                      key={idx}
                      className={
                        isUser
                          ? "text-foreground/80 text-xs font-normal"
                          : "text-primary font-medium font-serif-soft text-[13.5px] leading-relaxed"
                      }
                    >
                      <span className="font-semibold">
                        {isUser ? "Bạn" : (diviner?.ten ?? "Thầy")}:
                      </span>{" "}
                      {segment.text}
                    </div>
                  );
                })}

                {/* Realtime speaking text */}
                {shouldShowLivePartial(live.transcript, "diviner", live.divinerPartial) && (
                  <div className="text-primary font-medium font-serif-soft text-[13.5px] leading-relaxed animate-pulse">
                    <span className="font-semibold">{diviner?.ten ?? "Thầy"}:</span>{" "}
                    {live.divinerPartial}
                  </div>
                )}
                {shouldShowLivePartial(live.transcript, "user", live.userPartial) && (
                  <div className="text-foreground/60 text-xs italic animate-pulse">
                    <span className="font-semibold not-italic">Bạn:</span> {live.userPartial}
                  </div>
                )}
                <div ref={transcriptEndRef} />
              </div>
            )}

            {live.awaitingCast ? (
              <div className="flex flex-col items-center justify-center gap-4 py-4 w-full max-w-xs mx-auto animate-mystic-rise">
                <p className="text-xs font-serif-soft text-primary tracking-widest animate-pulse text-center">
                  ✦ TỊNH TÂM TỤ KHÍ · KHỞI LẬP QUẺ DỊCH ✦
                </p>

                {/* Visual Coins casting loader */}
                <Button
                  onMouseDown={startPress}
                  onMouseUp={stopPress}
                  onMouseLeave={stopPress}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    startPress();
                  }}
                  onTouchEnd={stopPress}
                  className="w-full h-16 rounded-2xl bg-gold-gradient text-black font-semibold shadow-gold active:scale-95 transition-all select-none duration-150 cursor-pointer flex flex-col justify-center gap-0.5 border border-amber-400/20"
                >
                  <span className="text-sm font-bold tracking-wide">
                    {tossingProgress > 0
                      ? `Đang tụ khí thỉnh quẻ... ${Math.round(tossingProgress * 100)}%`
                      : "Nhấn & Giữ để Gieo Quẻ 🪙"}
                  </span>
                  <span className="text-[9px] opacity-75 font-normal tracking-wider">
                    (Nhắm mắt nghĩ về câu hỏi & giữ 1.8s)
                  </span>
                </Button>

                <div className="w-full bg-amber-950/20 h-1.5 rounded-full overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-75"
                    style={{ width: `${tossingProgress * 100}%` }}
                  />
                </div>
              </div>
            ) : (
              <>
                {/* Glassmorphic input field */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (live.sendText(text)) setText("");
                  }}
                  className="w-full"
                >
                  <div
                    data-tour="boi-live-text-input"
                    className="relative flex items-center glass-input rounded-full px-3 py-1.5"
                  >
                    <Input
                      value={text}
                      onChange={(e) => setText(clampWords(e.target.value))}
                      placeholder={
                        live.paused ? "Đang tạm dừng…" : "Hoặc gõ câu hỏi nếu không tiện nói…"
                      }
                      disabled={live.paused}
                      className="flex-1 bg-transparent border-0 text-foreground placeholder:text-foreground/45 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm h-8 py-0 focus:outline-none disabled:opacity-50"
                    />
                    <Button
                      type="submit"
                      size="icon"
                      variant="ghost"
                      disabled={live.paused}
                      className="h-8 w-8 rounded-full text-foreground/60 hover:text-foreground hover:bg-foreground/10 active:scale-95 cursor-pointer"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </form>

                {/* Siri-like voice waves */}
                <div className="w-full h-10 flex items-center justify-center overflow-hidden">
                  <UserSoundwave
                    analyser={live.micAnalyser}
                    active={live.micEnabled && !live.paused}
                  />
                </div>

                {/* Hàng nút chính */}
                <div className="flex items-center justify-center gap-3">
                  <div data-tour="boi-live-results">
                    <LiveResultsButton results={live.toolResults} />
                  </div>
                  <div data-tour="boi-live-controls-desktop">
                    <LiveControls
                      compact
                      micEnabled={live.micEnabled}
                      onToggleMic={live.toggleMic}
                      paused={live.paused}
                      onTogglePause={live.togglePause}
                      transcriptVisible={live.transcriptVisible}
                      onToggleTranscript={() => live.setTranscriptVisible(!live.transcriptVisible)}
                      onEnd={live.end}
                      elapsedSeconds={live.elapsedSeconds}
                      billing={live.billing}
                      balanceLow={live.balanceLow}
                      showPausedHint={false}
                    />
                  </div>
                </div>
                {live.paused ? (
                  <p className="text-center text-xs font-medium text-primary animate-pulse">
                    Đang tạm dừng — không tính phí, mic đã tắt
                  </p>
                ) : null}
              </>
            )}

            {live.balanceLow && (
              <p className="text-center text-xs text-amber-500 font-medium animate-pulse">
                Số dư sắp hết — phiên sẽ tự kết thúc khi hết tiền.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── ENDED STATE (Standard summary + history transcripts) ─────────────────
  return (
    <div className="lg:grid lg:grid-cols-[360px_1fr] lg:gap-6">
      {/* Cột trái desktop: avatar + điều khiển */}
      <div className="hidden lg:block">
        <div className="sticky top-20 space-y-5">
          <LiveSessionSummary
            endInfo={live.endInfo!}
            onNewSession={live.reset}
            historySessionId={historyId}
          />
        </div>
      </div>

      <div className="flex min-h-[60vh] flex-col">
        {/* Summary mobile khi kết thúc */}
        <div className="mb-4 lg:hidden">
          <LiveSessionSummary
            endInfo={live.endInfo!}
            onNewSession={live.reset}
            historySessionId={historyId}
          />
        </div>

        {/* Stream hội thoại + tool cards */}
        <div className="flex-1" data-tour="boi-live-transcript">
          <LiveTranscript
            transcript={live.transcript}
            toolResults={live.toolResults}
            transcriptVisible={live.transcriptVisible}
            userPartial={live.userPartial}
            divinerPartial={live.divinerPartial}
            pendingTool={live.pendingTool}
            pendingToolLabel={live.pendingToolLabel}
            autoScroll={false}
            divinerName={diviner?.ten ?? "thầy"}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Lịch sử ────────────────────────────────────────────────────────────────

function HistoryList() {
  const qc = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const q = useInfiniteQuery({
    queryKey: ["live-sessions"],
    // 10 phiên mỗi lượt tải (trùng mặc định BE) — nút "Tải thêm" bên dưới lấy tiếp.
    queryFn: ({ pageParam }) => listLiveSessions(pageParam, LIVE_SESSIONS_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.page_size < last.total ? last.page + 1 : undefined,
  });
  const config = useQuery({ queryKey: ["live-config"], queryFn: getLiveConfig });
  const divinerName = (id: string) => config.data?.diviners.find((d) => d.id === id)?.ten ?? id;

  const del = useMutation({
    mutationFn: (id: number) => deleteLiveSession(id),
    onSuccess: () => {
      toast.success("Đã xoá phiên bói live");
      qc.invalidateQueries({ queryKey: ["live-sessions"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const items: LiveSessionRow[] = q.data?.pages.flatMap((p) => p.items) ?? [];

  if (q.isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;
  if (items.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Chưa có phiên bói live nào. Bắt đầu phiên đầu tiên ở tab Bói Live nhé.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((s) => (
        <Link
          key={s.id}
          to="/boi-live/$sessionId"
          params={{ sessionId: String(s.id) }}
          className="block"
        >
          <Card className="border-gold-soft/30 bg-card/60 transition hover:border-primary/50">
            <CardContent className="p-4 relative">
              {/* Header row: Diviner Name & Date & Delete Button */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">
                    Thầy: {divinerName(s.diviner_id)}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {s.started_at ? new Date(s.started_at).toLocaleString("vi-VN") : "—"}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 -mt-1 -mr-1 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/5 cursor-pointer rounded-full"
                  disabled={del.isPending && del.variables === s.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Nút xoá nằm ngay trong thẻ bấm-để-mở phiên, sát góc trên
                    // bên phải — chỗ ngón cái hay chạm khi cuộn danh sách.
                    void confirm({
                      title: "Xoá phiên bói live này?",
                      description: (
                        <>
                          Bản ghi phiên với {divinerName(s.diviner_id)}, lời luận và ghi chú buổi
                          xem sẽ bị xoá. Hành động này không thể hoàn tác và không hoàn tiền.
                        </>
                      ),
                      confirmLabel: "Xoá phiên",
                    }).then((ok) => {
                      if (ok) del.mutate(s.id);
                    });
                  }}
                >
                  {del.isPending && del.variables === s.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Metadata row: Clock, Coins, and Badge */}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-2 border-t border-gold-soft/10 text-xs">
                <span className="inline-flex items-center gap-1.5 tabular-nums text-muted-foreground">
                  <Clock className="h-3.5 w-3.5 text-primary/70" /> {formatMmSs(s.duration_seconds)}
                </span>
                <span className="inline-flex items-center gap-1.5 tabular-nums text-primary font-medium">
                  <Coins className="h-3.5 w-3.5 text-primary" /> {formatVnd(s.amount_charged)}
                </span>
                <Badge
                  variant="outline"
                  className="border-gold-soft/30 text-[10px] h-5 py-0 px-2 font-normal sm:ml-auto"
                >
                  {END_REASON_BADGE[s.end_reason ?? ""] ?? s.status}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
      {q.hasNextPage ? (
        <Button
          variant="outline"
          className="w-full"
          disabled={q.isFetchingNextPage}
          onClick={() => void q.fetchNextPage()}
        >
          {q.isFetchingNextPage ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Tải thêm
        </Button>
      ) : null}
      {confirmDialog}
    </div>
  );
}
